import { motion } from 'motion/react';
import { ArrowRight, Clock, Zap, Target, TrendingUp, Mail, MessageSquare, CheckCircle, Star } from 'lucide-react';
import { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackPlanSelected } from '@/lib/analytics';

export function ForSalesPage() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    trackPlanSelected('free', 'monthly', 0);
    if (isAuthenticated) {
      navigate('/account');
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleViewPricing = () => {
    navigate('/pricing');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] via-[#0F2847] to-[#1E3A8A]">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-[#60A5FA]/20 text-[#60A5FA] rounded-full text-sm font-semibold mb-6 border border-[#60A5FA]/30">
                Built for Sales Teams
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Close More Deals,<br />
                <span className="text-[#60A5FA]">Write Fewer Emails</span>
              </h1>
              <p className="text-xl sm:text-2xl text-white/80 max-w-3xl mx-auto mb-10">
                Stop spending 5 minutes per email reply. xReplAI generates multi-variant drafts that match your writing style in seconds -- directly inside Gmail and LinkedIn.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30 text-lg"
                >
                  Get Started Free
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20 text-lg"
                >
                  Compare Plans
                </button>
              </div>
              <p className="text-sm text-white/50 mt-4">
                Free plan includes 30 generations/month. No credit card required.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Sound Familiar?
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                The daily grind of a high-volume sales rep
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Clock,
                  title: '3+ hours/day on email',
                  description: 'Writing follow-ups, responding to prospects, and personalizing outreach eats your selling time.',
                  stat: '72%',
                  statLabel: 'of SDRs say email takes too long',
                },
                {
                  icon: Target,
                  title: 'Generic replies lose deals',
                  description: 'Copy-paste templates feel impersonal. Prospects can tell when a reply isn\'t written for them.',
                  stat: '2.6x',
                  statLabel: 'higher reply rate with personalized emails',
                },
                {
                  icon: TrendingUp,
                  title: 'Inconsistent tone kills trust',
                  description: 'Switching between assertive closes and gentle nurtures is mentally exhausting. One wrong tone can derail a deal.',
                  stat: '41%',
                  statLabel: 'of deals lost due to poor communication',
                },
              ].map((pain, i) => (
                <motion.div
                  key={i}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <pain.icon size={32} className="text-red-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{pain.title}</h3>
                  <p className="text-white/70 mb-6">{pain.description}</p>
                  <div className="border-t border-white/10 pt-4">
                    <span className="text-3xl font-bold text-[#60A5FA]">{pain.stat}</span>
                    <p className="text-xs text-white/50 mt-1">{pain.statLabel}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                How xReplAI Supercharges Your Sales Emails
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                AI-powered drafts that sound like you wrote them -- in seconds, not minutes
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: Zap,
                  title: 'Instant Multi-Variant Drafts',
                  description: 'Click one button and get 2-4 draft variants, each with a different tone and approach. Compare them side by side and insert your favorite.',
                  highlight: '30 seconds vs. 5 minutes',
                },
                {
                  icon: Mail,
                  title: 'Style Matching That Sounds Like You',
                  description: 'xReplAI learns your writing patterns -- vocabulary, sentence structure, formality -- and replicates them. Prospects will never know AI helped.',
                  highlight: 'Your voice, amplified',
                },
                {
                  icon: MessageSquare,
                  title: 'Context-Aware Sales Responses',
                  description: 'The AI reads the full thread context, detects if it\'s a cold follow-up, objection handling, or closing conversation, and tailors the draft accordingly.',
                  highlight: 'Smart context detection',
                },
                {
                  icon: Target,
                  title: 'Sales-Specific Content Package',
                  description: 'Specialized for outreach, follow-ups, proposals, objection handling, and closing. Every draft is optimized for the sales workflow.',
                  highlight: 'Built for selling',
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#60A5FA]/20 flex items-center justify-center">
                      <feature.icon size={24} className="text-[#60A5FA]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-white/70 mb-3">{feature.description}</p>
                      <span className="inline-block px-3 py-1 bg-[#60A5FA]/10 text-[#60A5FA] rounded-full text-sm font-medium border border-[#60A5FA]/20">
                        {feature.highlight}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Works Where You Work
              </h2>
              <p className="text-lg text-white/70">
                Chrome extension -- no new tabs, no copy-paste, no workflow disruption
              </p>
            </motion.div>

            <div className="space-y-8">
              {[
                { step: '1', title: 'Open any email in Gmail or LinkedIn message', description: 'xReplAI activates automatically when you click reply or compose.' },
                { step: '2', title: 'Click the xReplAI button', description: 'The extension reads the conversation context, detects the email type, and suggests appropriate goals and tones.' },
                { step: '3', title: 'Review and insert your draft', description: 'Compare 2-4 variants, pick the best one, and insert it into the compose box with one click. Edit if needed and send.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#60A5FA] flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-[#60A5FA]/30">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-white/70">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Social Proof / Testimonials Placeholder */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Why Sales Professionals Choose xReplAI
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  metric: '70%',
                  label: 'Less time on email replies',
                  description: 'What took 5 minutes now takes under 30 seconds',
                },
                {
                  metric: '4x',
                  label: 'More emails per hour',
                  description: 'Handle higher volume without sacrificing quality',
                },
                {
                  metric: '100%',
                  label: 'Your voice, every time',
                  description: 'Style matching ensures every draft sounds authentically you',
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="text-center p-8 bg-white/5 rounded-2xl border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl font-bold text-[#60A5FA] mb-2">{stat.metric}</div>
                  <div className="text-lg font-semibold text-white mb-2">{stat.label}</div>
                  <div className="text-sm text-white/60">{stat.description}</div>
                </motion.div>
              ))}
            </div>

            {/* Testimonial placeholder -- will be populated with real testimonials */}
            <motion.div
              className="mt-12 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Star size={32} className="text-yellow-400 mx-auto mb-4" />
              <blockquote className="text-lg text-white/90 italic mb-4 max-w-2xl mx-auto">
                "xReplAI has completely changed how I handle my inbox. I used to spend the first 2 hours of my day just on email replies. Now I knock them out in 20 minutes and spend the rest of my time selling."
              </blockquote>
              <p className="text-white/60 text-sm">
                -- Early Access User, SDR at a SaaS Company
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Teaser */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Start Free, Scale When Ready
              </h2>
              <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
                Try xReplAI with 30 free generations per month. When you're ready for more, upgrade to Pro for unlimited drafts, style matching, and premium AI models.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">Free</h3>
                  <div className="text-3xl font-bold text-white mb-1">$0</div>
                  <p className="text-sm text-white/60 mb-4">forever</p>
                  <ul className="text-left text-sm text-white/70 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 30 generations/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> Gmail + LinkedIn</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 2 draft variants</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-[#1E3A8A] to-[#60A5FA] rounded-2xl p-6 border border-[#60A5FA]/50 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#60A5FA] text-white rounded-full text-xs font-semibold">
                    Most Popular
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
                  <div className="text-3xl font-bold text-white mb-1">$30<span className="text-lg font-normal">/mo</span></div>
                  <p className="text-sm text-white/70 mb-4">or $252/year (save 30%)</p>
                  <ul className="text-left text-sm text-white/90 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> 500 generations/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> Style matching</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> Sales package included</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> GPT-4o + Claude + Grok</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30"
                >
                  Get Started Free
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  Compare All Plans
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Get Your Time Back?
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
                Join sales professionals who are writing better emails in less time. Set up takes under 2 minutes.
              </p>
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#60A5FA] text-white font-bold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30 text-lg"
              >
                Start Free Today
                <ArrowRight size={22} />
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
}
